import { resolveSourceProjectName } from '../drillingCampaignHelpers';

/**
 * Resolve the sample progress row for a campaign tab (mirrors DrillingDashboard).
 */
export function resolveSampleMetricsRow(sampleProgressBundle, selectedProjectId) {
  if (!sampleProgressBundle) return null;

  if (selectedProjectId === 'all') {
    return sampleProgressBundle.aggregated ?? null;
  }

  const rows = sampleProgressBundle.projects || [];
  return (
    rows.find(
      (p) =>
        p.source_project_id != null &&
        String(p.source_project_id) === String(selectedProjectId)
    ) ?? null
  );
}

function pct(numerator, denominator) {
  const num = Number(numerator) || 0;
  const den = Number(denominator) || 0;
  if (den <= 0) return 0;
  return Math.min((num / den) * 100, 100);
}

/**
 * Normalize mv_sample_progress row with derived percentages for report UI/PDF.
 */
export function buildSampleProgressForReport(sampleProgressBundle, selectedProjectId) {
  const row = resolveSampleMetricsRow(sampleProgressBundle, selectedProjectId);
  if (!row) return null;

  const designated = Number(row.total_samples_designated) || 0;
  const bagged = Number(row.total_samples_bagged) || 0;
  const atLab = Number(row.total_samples_received_by_lab) || 0;
  const finalized = Number(row.total_samples_finalized_by_lab) || 0;
  const qaqcPassed = Number(row.total_samples_qaqc_passed) || 0;
  const leachReceived = Number(row.sequential_leach_samples_received_by_lab) || 0;
  const leachFinalized = Number(row.sequential_leach_samples_finalized_by_lab) || 0;

  const campaignLabel =
    selectedProjectId === 'all'
      ? row.source_project_name || 'All projects'
      : resolveSourceProjectName(selectedProjectId, sampleProgressBundle?.projects) ||
        row.source_project_name ||
        'Selected project';

  return {
    raw: row,
    campaignLabel,
    designated,
    bagged,
    atLab,
    finalized,
    qaqcPassed,
    leachReceived,
    leachFinalized,
    pctBagged: pct(bagged, designated),
    pctAtLab: pct(atLab, designated),
    pctFinalized: pct(finalized, designated),
    pctQaqcPassed: pct(qaqcPassed, designated),
    pctLeachFinalized: pct(leachFinalized, leachReceived),
    sequential_leach_samples_received_by_lab: leachReceived,
    sequential_leach_samples_finalized_by_lab: leachFinalized,
    total_samples_designated: designated,
    total_samples_bagged: bagged,
    total_samples_received_by_lab: atLab,
    total_samples_finalized_by_lab: finalized,
    total_samples_qaqc_passed: qaqcPassed,
    source_project_name: row.source_project_name,
  };
}

export function parseReportDateInput(dateInput) {
  if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
  }
  if (typeof dateInput === 'string') {
    const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, month, day);
    }
  }
  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function formatReportDate(dateInput) {
  const date = parseReportDateInput(dateInput);
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Cover footer placeholder, e.g. "AUGUST 2026" */
export function formatReportMonthYear(dateInput, { uppercase = false } = {}) {
  const date = parseReportDateInput(dateInput);
  if (!date) return '';
  const label = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
  });
  return uppercase ? label.toUpperCase() : label;
}

export function toDateInputValue(date = new Date()) {
  const d = date instanceof Date ? date : parseReportDateInput(date) || new Date();
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const y = local.getFullYear();
  const m = String(local.getMonth() + 1).padStart(2, '0');
  const day = String(local.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
